import { Calculator, Wrench, type LucideIcon } from "lucide-react";
import {
  findTool,
  toolCategories,
  toolCount,
  type ToolEntry,
} from "@/data/toolsCatalog";

export type GoalLink = {
  label: string;
  href: string;
  description: string;
  icon: LucideIcon;
};

export type GoalCategoryLink = {
  label: string;
  href: string;
  icon: LucideIcon;
};

export type Goal = {
  id: string;
  question: string;          // shown in nav (plain English)
  shortLabel: string;        // very short, for tight spaces
  blurb: string;             // 1-line panel intro
  primaryHref: string;       // "Start here" CTA target
  primaryLabel: string;
  links: GoalLink[];
  /** Compact category chips linking into the /tools directory. */
  categoryLinks?: GoalCategoryLink[];
};

/** Pull entries out of the shared catalog so labels never drift. */
function pick(...hrefs: string[]): GoalLink[] {
  return hrefs
    .map((href) => findTool(href))
    .filter((t): t is ToolEntry => Boolean(t))
    .map((t) => ({
      label: t.label,
      href: t.href,
      description: t.description,
      icon: t.icon,
    }));
}

const categoryLinks: GoalCategoryLink[] = toolCategories.map((c) => ({
  label: c.title,
  href: `/tools#${c.id}`,
  icon: c.icon,
}));

export const goals: Goal[] = [
  {
    id: "tools",
    question: "Tools",
    shortLabel: "Tools",
    blurb: `Plan, qualify, and explore. ${toolCount} free tools and guides.`,
    primaryHref: "/tools",
    primaryLabel: `Browse all ${toolCount} tools`,
    links: pick(
      "/affordability-simulator",
      "/grant-finder",
      "/credit-readiness",
      "/down-payment-planner",
      "/home-value",
      "/experts",
    ),
    categoryLinks,
  },
  {
    id: "calculators",
    question: "Calculators",
    shortLabel: "Calculators",
    blurb: "Run the numbers. Mortgage, refi, rent vs buy, and more.",
    primaryHref: "/mortgage-calculators",
    primaryLabel: "Open all calculators",
    links: pick(
      "/mortgage-calculators",
      "/rent-vs-buy",
      "/refi-optimizer",
      "/closing-costs",
      "/tools/cash-to-close",
      "/tools/down-payment-assistance-estimate",
      "/relocation-calculator",
      "/tools/right-time-to-buy",
    ),
    categoryLinks,
  },
];

export const goalIcons: Record<string, LucideIcon> = {
  tools: Wrench,
  calculators: Calculator,
};
